/**
 * `/sessions/{id}` — the door a conversation link comes in through, which sends
 * the nurse to the stay that conversation is about.
 *
 * adr/0023 moved this app's URL onto the SUBJECT: the board lives at
 * `/stays/{id}` and a conversation rides as `?c=<id>`. Links minted before that
 * — the main app's session rail, a shared link, a browser history entry — still
 * point here, and `agentSessionUrl` still builds this shape for every agent with
 * a subdomain. So this route stays and resolves: a conversation names a patient,
 * the patient has an admitted stay, and that stay is what the nurse asked to
 * see. It replaces the history entry, so Back does not bounce her through the
 * redirect.
 *
 * It is NOT a second way to open a board. It renders no plan of its own; the
 * only thing it decides is which stay `/stays/{id}` means.
 */

import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { ReturnToMainAgent } from '@frontend/components/ReturnToMainAgent'
import { Spinner } from '@frontend/components/ui/spinner'
import { useAdmittedStay } from '@frontend/hooks/useEncounters'
import { useSession } from '@frontend/hooks/useSessions'

import { CONVERSATION_PARAM } from './Stay'

function Chrome({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-dvh flex-col bg-[var(--color-surface-canvas,#ffffff)]">
      <header className="flex items-center gap-2 border-b border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <ReturnToMainAgent className="-ml-2" />
      </header>
      <div className="flex flex-1 items-center justify-center p-8 text-center">{children}</div>
    </div>
  )
}

export default function CarePlannerSession() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const sessionId = id ?? ''
  const { data: session, isLoading: sessionLoading, error: sessionError } = useSession(sessionId)
  const subject = session?.session.subject
  const patientId = subject?.type === 'patient' ? subject.id : undefined
  const { data: stay, isLoading: stayLoading, error: stayError } = useAdmittedStay(patientId)

  useEffect(() => {
    if (!stay || !sessionId) return
    navigate(`/stays/${stay.id}?${CONVERSATION_PARAM}=${encodeURIComponent(sessionId)}`, {
      replace: true,
    })
  }, [stay, sessionId, navigate])

  if (!id) return null

  const error = sessionError ?? stayError
  if (error) {
    return (
      <Chrome>
        <p className="max-w-sm text-[17px] text-[var(--color-status-danger,#b3261e)]">
          {t('carePlan.conversationUnresolved', 'This conversation could not be opened.')}
        </p>
      </Chrome>
    )
  }

  if (sessionLoading || (patientId && stayLoading)) {
    return (
      <Chrome>
        <Spinner className="h-6 w-6 text-[var(--color-surface-mute)]" />
        <span className="sr-only">{t('common.loading', 'Loading...')}</span>
      </Chrome>
    )
  }

  // The conversation names no patient, or the patient it names has no open
  // stay. Both are real states with the same consequence: there is no care plan
  // for this conversation to be about, and picking some other stay would open a
  // different patient's plan under a link that named this one.
  if (!stay) {
    return (
      <Chrome>
        <p className="max-w-sm text-[17px] text-[var(--color-surface-mute)]">
          {t(
            'carePlan.conversationHasNoStay',
            'This conversation is not about an admitted patient, so there is no care plan to open.',
          )}
        </p>
      </Chrome>
    )
  }

  // The effect above is navigating. One spinner frame, not a blank screen.
  return (
    <Chrome>
      <Spinner className="h-6 w-6 text-[var(--color-surface-mute)]" />
      <span className="sr-only">{t('common.loading', 'Loading...')}</span>
    </Chrome>
  )
}
